import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Folders } from 'lucide-react';
import AccumulationChart from '../AccumulationChart';
import DecumulationChart from '../DecumulationChart';
import MonteCarloFanChart from '../MonteCarloFanChart';
import IncomeVsExpensesChart from '../IncomeVsExpensesChart';
import FundingRatioGauge from '../FundingRatioGauge';
import WithdrawalStrategyComparison from '../WithdrawalStrategyComparison';
import ScenarioComparison from '../ScenarioComparison';

/**
 * Analysis Tabs - Wraps all analysis/results visualizations
 */
export default function AnalysisTabs({
  results,
  inputs,
  retirementAccounts,
  currencySymbol,
  statePensionEstimate,
  activeAnalysisTab,
  setActiveAnalysisTab,
  onLoadScenario
}) {
  if (!results) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          Configure your retirement inputs and click "Recalculate" to see your retirement analysis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Items Grid */}
      {results.actionItems && results.actionItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.actionItems.slice(0, 4).map((action, index) => (
            <Alert
              key={index}
              variant={
                action.category === 'urgent' ? 'destructive' :
                action.category === 'warning' ? 'warning' : 'default'
              }
            >
              <span className="text-2xl">{action.icon}</span>
              <AlertTitle>{action.title}</AlertTitle>
              <AlertDescription>{action.description}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Analysis Tabs */}
      <Tabs value={activeAnalysisTab} onValueChange={setActiveAnalysisTab}>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <TabsList className="inline-flex w-full min-w-max sm:grid sm:grid-cols-6 sm:min-w-0">
            <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">
              Overview
            </TabsTrigger>
            <TabsTrigger value="accumulation" className="text-xs sm:text-sm whitespace-nowrap">
              Accumulation
            </TabsTrigger>
            <TabsTrigger value="drawdown" className="text-xs sm:text-sm whitespace-nowrap">
              Drawdown
            </TabsTrigger>
            <TabsTrigger value="montecarlo" className="text-xs sm:text-sm whitespace-nowrap">
              Monte Carlo
            </TabsTrigger>
            <TabsTrigger value="strategies" className="text-xs sm:text-sm whitespace-nowrap">
              Strategies
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="text-xs sm:text-sm whitespace-nowrap">
              <Folders className="h-3 w-3 mr-1 inline" />
              Scenarios
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FundingRatioGauge
              fundingRatio={results.metrics.fundingRatio}
              requiredPortfolio={results.metrics.requiredPortfolio}
              totalAtRetirement={results.accumulationResult.totalAtRetirement}
              currencySymbol={currencySymbol}
            />

            <IncomeVsExpensesChart
              drawdownData={results.drawdownResult}
              desiredIncome={inputs.desiredAnnualIncome}
              essentialSpending={inputs.essentialSpending}
              discretionarySpending={inputs.discretionarySpending}
              currencySymbol={currencySymbol}
              retirementAge={inputs.retirementAge}
              statePensionAge={inputs.statePensionAge}
              country={inputs.country}
            />
          </div>

          {/* Key Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Key Retirement Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Income Replacement</div>
                  <div className="text-2xl font-bold">
                    {Math.round(results.metrics.incomeReplacementRatio * 100)}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Withdrawal Rate</div>
                  <div className="text-2xl font-bold">
                    {results.metrics.sustainableWithdrawalRate.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Monte Carlo Success</div>
                  <div className={`text-2xl font-bold ${
                    results.monteCarloResult.successRate >= 90 ? 'text-green-600' :
                    results.monteCarloResult.successRate >= 75 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {results.monteCarloResult.successRate}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Years Covered</div>
                  <div className="text-2xl font-bold">
                    {results.metrics.yearsCovered.toFixed(0)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accumulation" className="mt-4">
          <AccumulationChart
            data={results.accumulationResult}
            accounts={retirementAccounts}
            currencySymbol={currencySymbol}
          />
        </TabsContent>

        <TabsContent value="drawdown" className="mt-4">
          <DecumulationChart
            data={results.drawdownResult}
            statePension={inputs.includeStatePension ? statePensionEstimate : 0}
            inputs={inputs}
            currencySymbol={currencySymbol}
            country={inputs.country}
          />
        </TabsContent>

        <TabsContent value="montecarlo" className="mt-4">
          <MonteCarloFanChart
            data={results.monteCarloResult}
            successRate={results.monteCarloResult.successRate}
            currencySymbol={currencySymbol}
          />
        </TabsContent>

        <TabsContent value="strategies" className="mt-4">
          <WithdrawalStrategyComparison
            data={results.withdrawalStrategyComparison}
            currentStrategy={inputs.withdrawalStrategy}
            currencySymbol={currencySymbol}
          />
        </TabsContent>

        <TabsContent value="scenarios" className="mt-4">
          <ScenarioComparison
            currencySymbol={currencySymbol}
            onLoadScenario={onLoadScenario}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
